import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { prefetchAppRouteChunks } from '../lib/prefetchRoutes.js'
import { dexFetch } from '../lib/dexApi.js'
import { absoluteDexUrl } from '../lib/apiBase.js'

const AuthContext = createContext(null)

const STORAGE_KEY = 'lotos_dashboard_auth'
const SESSION_HOURS = 12
const SESSION_MS = SESSION_HOURS * 60 * 60 * 1000
const API_AUTH = '/api/auth'
/** Poll profile so role / visible groups update after admin changes rights without re-login. */
const ACCESS_POLL_MS = 12_000

function userAccessSnapshot(u) {
  if (!u) return ''
  try {
    return JSON.stringify({
      role: String(u.role ?? '').toLowerCase(),
      vg: [...(u.visibleGroupIds || [])].map(String).sort(),
      cg: [...(u.clientGroupIds || [])].map(String).sort(),
      cg1: u.clientGroupId != null ? String(u.clientGroupId) : null,
      tc: [...(u.traderClientIds || [])].map(String).sort(),
      kbc: u.canAccessKnowledgeCatalog === true,
      cal: u.canAccessLogs === true,
      apg: [...(u.adminProjectGroupIds || [])].map(String).sort(),
      wr: u.canRevealWalletSecret === true,
      tgs: u.canAccessTokenGroupSettings === true,
      eas: u.canAccessExchangeApiSettings === true,
      kab: u.knowledgeAclBypass === true,
      caw: u.canAccessWarmup === true,
      wes: u.canWarmupExportSecrets === true,
      wew: u.canWarmupExternalWallets === true,
      cad: u.canAccessDex === true,
      cac: u.canAccessCex === true,
      cact: u.canAccessCexTokens === true,
      clb: u.canLaunchCexBots === true,
    })
  } catch {
    return String(u.id)
  }
}

function isValidTimestamp(val) {
  const t = typeof val === 'number' ? val : Number(val)
  return Number.isFinite(t) && t > 0
}

function isSessionExpired(loggedInAt) {
  if (!isValidTimestamp(loggedInAt)) return false
  return Date.now() - loggedInAt > SESSION_MS
}

function normalizeUserFromApi(u) {
  if (!u) return u
  if (!u.avatar) return { ...u }
  return { ...u, avatar: absoluteDexUrl(u.avatar) }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const intervalRef = useRef(null)

  const clearSession = useCallback(() => {
    setUser(null)
    dexFetch(`${API_AUTH}/logout`, { method: 'POST' }).catch(() => {})
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const data = JSON.parse(raw)
        if (!data?.user) return
        let loggedInAt = data.loggedInAt
        if (!loggedInAt || !isValidTimestamp(loggedInAt)) {
          loggedInAt = Date.now()
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, loggedInAt }))
          } catch (_) {}
        }
        if (isSessionExpired(loggedInAt)) {
          try {
            localStorage.removeItem(STORAGE_KEY)
          } catch (_) {}
          return
        }

        const res = await dexFetch('/api/users/me')
        if (cancelled) return
        if (res.ok) {
          const me = await res.json().catch(() => null)
          if (cancelled) return
          if (me) {
            const m = normalizeUserFromApi(me)
            setUser(m)
            try {
              const prev = localStorage.getItem(STORAGE_KEY)
              if (prev) {
                const parsed = JSON.parse(prev)
                localStorage.setItem(
                  STORAGE_KEY,
                  JSON.stringify({ ...parsed, user: m, loggedInAt: parsed.loggedInAt }),
                )
              }
            } catch (_) {}
          } else {
            setUser(normalizeUserFromApi(data.user))
          }
        } else if (res.status === 401) {
          clearSession()
        } else {
          setUser(normalizeUserFromApi(data.user))
        }
      } catch (_) {
        // ignore corrupt storage / parse errors
      } finally {
        if (!cancelled) setInitialized(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clearSession])

  useEffect(() => {
    if (!user) return
    intervalRef.current = setInterval(() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) {
          clearSession()
          return
        }
        const data = JSON.parse(raw)
        if (isSessionExpired(data?.loggedInAt)) clearSession()
      } catch (_) {}
    }, 60 * 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [user, clearSession])

  useEffect(() => {
    if (!initialized || !user?.id) return
    let cancelled = false
    const tick = async () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
        const res = await dexFetch('/api/users/me')
        if (cancelled) return
        if (res.status === 401) {
          clearSession()
          window.dispatchEvent(new Event('auth:sessionInvalid'))
          return
        }
        if (!res.ok) return
        const me = await res.json().catch(() => null)
        if (!me || cancelled) return
        const normalized = normalizeUserFromApi(me)
        setUser((prev) => {
          if (!prev || prev.id !== normalized.id) return prev
          if (userAccessSnapshot(prev) === userAccessSnapshot(normalized)) return prev
          try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (raw) {
              const data = JSON.parse(raw)
              localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ ...data, user: normalized, loggedInAt: data.loggedInAt }),
              )
            }
          } catch (_) {}
          queueMicrotask(() => window.dispatchEvent(new CustomEvent('lotos:accessUpdated')))
          return normalized
        })
      } catch (_) {}
    }
    const id = setInterval(tick, ACCESS_POLL_MS)
    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onVis)
    return () => {
      cancelled = true
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onVis)
    }
  }, [initialized, user?.id, clearSession])

  /** Lazy route chunks: start loading as soon as user is known (login + /api/users/me + F5). */
  useEffect(() => {
    if (!user?.id) return
    try {
      prefetchAppRouteChunks()
    } catch (_) {}
  }, [user?.id])

  /** Plain `fetch` calls (e.g. GroupsContext) do not clear session on 401; they dispatch this event instead. */
  useEffect(() => {
    const onSessionInvalid = () => clearSession()
    window.addEventListener('auth:sessionInvalid', onSessionInvalid)
    return () => window.removeEventListener('auth:sessionInvalid', onSessionInvalid)
  }, [clearSession])

  const login = useCallback(async (loginVal, password) => {
    try {
      const res = await dexFetch(`${API_AUTH}/login`, {
        method: 'POST',
        body: JSON.stringify({ login: loginVal, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 429) {
        throw new Error(data?.error || 'Too many login attempts. Try again later.')
      }
      if (!res.ok) return false
      if (!data?.user) return false
      const u = normalizeUserFromApi(data.user)
      const loggedInAt = Date.now()
      const stored = { user: u, loggedInAt }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
      if ((u?.role ?? '').toLowerCase() === 'client') {
        try { localStorage.removeItem('lotos_dashboard_groups') } catch (_) {}
      }
      setUser(u)
      return true
    } catch (err) {
      if (err?.message) throw err
      return false
    }
  }, [])

  const logout = useCallback(() => {
    clearSession()
  }, [clearSession])

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return prev
      const merged = { ...prev, ...updates }
      const next = merged.avatar
        ? { ...merged, avatar: absoluteDexUrl(merged.avatar) }
        : merged
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
          const data = JSON.parse(raw)
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, user: next }))
        }
      } catch (_) {}
      return next
    })
  }, [])

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await dexFetch('/api/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
    if (res.status === 401) {
      clearSession()
      throw new Error('Session expired. Sign in again.')
    }
    const data = await res.json()
    if (!res.ok) throw new Error(data?.error || 'Password change failed')
  }, [clearSession])

  const getAuthHeadersForRequest = useCallback(() => ({}), [])

  const fetchWithAuth = useCallback(
    async (url, options = {}) => {
      /** `/api/...` paths are always routed to the DEX backend (authority for users, logs,
       *  knowledge, warmup, distributor, settings, groups). Absolute URLs pass through. */
      const res = /^https?:\/\//.test(url)
        ? await fetch(url, {
            ...options,
            credentials: 'include',
            cache: options.cache ?? 'no-store',
          })
        : await dexFetch(url, options)
      if (res.status === 401) {
        clearSession()
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('auth:sessionInvalid'))
      }
      return res
    },
    [clearSession]
  )

  const isClientUser = (user?.role ?? '').toLowerCase() === 'client'
  /** Как на сервере: у клиентов CEX только при `cexAccess: true`; у staff — «не false» (в т.ч. старые сессии без поля). */
  const canAccessCex =
    !user ? false : isClientUser ? user.canAccessCex === true : user.canAccessCex !== false

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        changePassword,
        getAuthHeaders: getAuthHeadersForRequest,
        fetchWithAuth,
        isAuthenticated: !!user,
        authInitialized: initialized,
        isAdmin: ['admin', 'ceo'].includes((user?.role ?? '').toLowerCase()),
        isCeo: (user?.role ?? '').toLowerCase() === 'ceo',
        isClient: isClientUser,
        clientGroupId: user?.clientGroupId ?? null,
        canViewLogs: user?.canAccessLogs === true,
        canAccessWarmup: user?.canAccessWarmup === true,
        canWarmupExportSecrets: user?.canWarmupExportSecrets === true,
        canWarmupExternalWallets: user?.canWarmupExternalWallets === true,
        canAccessDex: user ? user?.canAccessDex !== false : true,
        canAccessCex,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
